<template>
  <div class="newsletter-form">
    <form @submit.prevent="handleSubmit" class="flex flex-col sm:flex-row gap-4">
      <div class="flex-1">
        <input
          type="text"
          v-model="formData.firstName"
          :class="{ 'border-red-500': errors.firstName }"
          class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand"
          placeholder="Your Name"
          required
        />
        <p v-if="errors.firstName" class="mt-1 text-sm text-red-500">
          {{ errors.firstName }}
        </p>
      </div>
      <div class="flex-1">
        <input
          type="email"
          v-model="formData.email"
          :class="{ 'border-red-500': errors.email }"
          class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand"
          placeholder="Type your email"
          required
        />
        <p v-if="errors.email" class="mt-1 text-sm text-red-500">{{ errors.email }}</p>
      </div>
      <div>
        <button
          type="submit"
          :disabled="isSubmitting"
          class="w-full sm:w-auto px-6 py-3 bg-brand hover:bg-brand-dark text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50"
        >
          <span v-if="isSubmitting">Sending...</span>
          <span v-else>Send Me A Reminder</span>
        </button>
      </div>
    </form>

    <!-- Success Message -->
    <div
      v-if="successMessage"
      class="mt-4 p-4 bg-green-50 text-green-700 rounded-lg text-center"
    >
      {{ successMessage }}
    </div>

    <!-- Error Message -->
    <div
      v-if="errorMessage"
      class="mt-4 p-4 bg-red-50 text-red-700 rounded-lg text-center"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from "vue";

const formData = reactive({
  firstName: "",
  email: ""
});

const errors = reactive({
  firstName: "",
  email: ""
});

const isSubmitting = ref(false);
const successMessage = ref("");
const errorMessage = ref("");

const validateEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const validateForm = () => {
  let isValid = true;
  errors.firstName = "";
  errors.email = "";

  if (!formData.firstName.trim()) {
    errors.firstName = "Name is required";
    isValid = false;
  }

  if (!formData.email.trim()) {
    errors.email = "Email is required";
    isValid = false;
  } else if (!validateEmail(formData.email)) {
    errors.email = "Please enter a valid email address";
    isValid = false;
  }

  return isValid;
};

const handleSubmit = async () => {
  errorMessage.value = "";
  successMessage.value = "";

  if (!validateForm()) {
    return;
  }

  isSubmitting.value = true;

  try {
    console.log("Submitting newsletter form:", formData);

    const response = await fetch("/api/subscribe", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        firstName: formData.firstName,
        email: formData.email
      })
    });

    const data = await response.json();

    if (data.success) {
      successMessage.value =
        data.message || "SUCCESS! Please check your email for some exciting information.";
      // Reset form
      formData.firstName = "";
      formData.email = "";
    } else {
      throw new Error(data.error || "Failed to subscribe");
    }
  } catch (error) {
    console.error("Newsletter form error:", error);
    if (error.message == "Email address already exists in the system.") {
      successMessage.value =
        "Thanks for signing up, but it looks like you are already on our list!";
    } else {
      errorMessage.value =
        error.message || "Sorry, something went wrong. Please try again later.";
    }
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.newsletter-form {
  width: 100%;
  max-width: 48rem;
  margin-left: auto;
  margin-right: auto;
}
</style>
